// src/components/SponsorApplyForm.js
//
// スポンサー掲載の申込フォーム。LINEログイン済みの会員のみが送信できる。
//
// 【ルールとの対応】
//   firestore.rules の sponsor_applications は create の条件として
//     applicantId == request.auth.uid / status == 'pending' /
//     amount・paidAt・approvedAt 等を含まないこと
//   を要求している。この画面はそれに合わせた形でのみ書き込む。
//   金額や掲載期間は管理者が入金確認時に入れるため、ここでは送らない。
//
// 【文言とカテゴリ】
//   settings/sponsors から読み込む（管理画面 → 表示設定 で編集可）。
//   未設定のときは sponsorDefaults.js の初期値を使う。
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, doc, getDoc, getCountFromServer, serverTimestamp } from 'firebase/firestore';
import { COLORS } from '../theme';
import { DEFAULT_SPONSOR_CATEGORIES, mergeTexts, applyPlaceholders } from '../sponsorDefaults';

// ---- 会員数のキャッシュ ---------------------------------------------
//   getCountFromServer は集計クエリで、一致する1,000件ごとに読み取り1回として
//   課金される。609名なら「1回」で済むため、全件取得（609回）とは桁違いに安い。
//   さらにセッション中はここに保持して、開くたびの問い合わせを避ける。
let _memberCountCache = { value: null, at: 0 };
const COUNT_CACHE_MS = 60 * 60 * 1000;   // 60分

// 掲載イメージ。実際のカードとクーポン画面をそのまま縮小して見せる。
//   入力途中の内容（社名・PR文・クーポン）があればそれを反映するので、
//   「自分の店がどう出るか」を見ながら申し込める。
const PreviewCard = ({ name, catchText, coupon }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: '12px', padding: '12px',
        background: 'white', border: '1px solid #eee', borderRadius: '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
    }}>
        <div style={{
            width: '56px', height: '56px', background: '#f5f5f5', borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '22px', flexShrink: 0, border: '1px solid #eee'
        }}>🏪</div>
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <span style={{ fontSize: '9px', background: '#eee', color: '#888', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>PR</span>
                {coupon && (
                    <span style={{ fontSize: '10px', background: '#fff3e0', color: '#e65100', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>会員特典あり</span>
                )}
            </div>
            <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {name || '○○整骨院'}
            </div>
            <div style={{ fontSize: '11px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {catchText || 'スポーツ専門。選手の身体を支えます'}
            </div>
        </div>
        <span style={{ fontSize: '16px', color: '#ccc', flexShrink: 0 }}>›</span>
    </div>
);

const PreviewCoupon = ({ name, coupon }) => (
    <div style={{ background: 'white', borderRadius: '12px', padding: '16px', textAlign: 'center', border: '1px solid #eee' }}>
        <div style={{ fontSize: '10px', color: '#999', marginBottom: '4px' }}>会員クーポン</div>
        <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333', marginBottom: '10px' }}>{name || '○○整骨院'}</div>
        <div style={{ border: '2px dashed #ff9800', borderRadius: '8px', padding: '12px 8px', marginBottom: '10px', background: '#fff8e1' }}>
            <div style={{ fontSize: '10px', color: '#e65100', marginBottom: '4px' }}>クーポンコード</div>
            <div style={{ fontSize: '18px', fontWeight: '900', letterSpacing: '2px', color: '#e65100' }}>REVENGERS</div>
            <div style={{ fontSize: '11px', color: '#5d4037', marginTop: '6px', fontWeight: 'bold' }}>
                {coupon || 'ご提示で施術料10%OFF'}
            </div>
        </div>
        <div style={{ background: '#263238', color: 'white', borderRadius: '6px', padding: '8px' }}>
            <div style={{ fontSize: '9px', opacity: 0.7 }}>ご提示者</div>
            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>リベンジャーズ太郎</div>
            <div style={{ fontSize: '9px', opacity: 0.7 }}>提示時刻（毎秒更新）</div>
            <div style={{ fontSize: '15px', fontWeight: 'bold', letterSpacing: '1px' }}>14:32:07</div>
        </div>
    </div>
);

const Row = ({ label, required, children, hint }) => (
    <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '5px', color: '#444' }}>
            {label}
            {required && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
        </label>
        {children}
        {hint && <div style={{ fontSize: '10px', color: '#999', marginTop: '4px', lineHeight: '1.6' }}>{hint}</div>}
    </div>
);

const inputStyle = { width: '100%', padding: '10px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px' };

const SponsorApplyForm = ({ userInfo, lineUser, setActiveTab }) => {
    const [form, setForm] = useState({
        companyName: '', contactName: '', email: '', phone: '',
        categoryId: '', prText: '', url: '', couponPlan: '', transferName: '', note: ''
    });
    const [categories, setCategories] = useState(DEFAULT_SPONSOR_CATEGORIES);
    const [texts, setTexts] = useState(mergeTexts(null));
    const [memberCount, setMemberCount] = useState(_memberCountCache.value);
    const [isSending, setIsSending] = useState(false);
    const [isDone, setIsDone] = useState(false);

    // 管理画面で設定した文言・カテゴリを読み込む
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'settings', 'sponsors'));
                if (!alive || !snap.exists()) return;
                const d = snap.data();
                const cats = (Array.isArray(d.categories) && d.categories.length)
                    ? d.categories.slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
                    : DEFAULT_SPONSOR_CATEGORIES;
                setCategories(cats);
                setTexts(mergeTexts(d.texts));
            } catch (e) {
                console.error('スポンサー設定の取得に失敗:', e);
            }
        })();
        return () => { alive = false; };
    }, []);

    // 現在の登録者数を取得する（文章の {memberCount} に差し込む）
    useEffect(() => {
        let alive = true;
        (async () => {
            const now = Date.now();
            if (_memberCountCache.value && (now - _memberCountCache.at) < COUNT_CACHE_MS) {
                setMemberCount(_memberCountCache.value);
                return;
            }
            try {
                const snap = await getCountFromServer(collection(db, 'users'));
                const n = snap.data().count;
                _memberCountCache = { value: n, at: now };
                if (alive) setMemberCount(n);
            } catch (e) {
                // 失敗しても画面は壊さない。既定値が使われる。
                console.error('会員数の取得に失敗:', e);
            }
        })();
        return () => { alive = false; };
    }, []);

    // カテゴリ一覧が確定したら、未選択なら先頭を初期値にする
    useEffect(() => {
        if (!form.categoryId && categories.length > 0) {
            setForm(prev => ({ ...prev, categoryId: categories[0].id }));
        }
    }, [categories, form.categoryId]);

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    // App.js の userInfo は userId フィールドを持つ（id ではない）。
    //   ルールの applicantId == request.auth.uid を満たす必要があるため、
    //   LINEログインのUID（= Firebase Auth の uid）をそのまま使う。
    const uid = lineUser?.userId || userInfo?.userId || null;

    const handleSubmit = async () => {
        if (!uid) { alert('ログイン情報が取得できませんでした。アプリを開き直してください。'); return; }
        if (!form.companyName.trim()) { alert('会社名・店舗名を入力してください'); return; }
        if (!form.contactName.trim()) { alert('ご担当者名を入力してください'); return; }
        if (!form.email.trim() && !form.phone.trim()) { alert('メールアドレスか電話番号のどちらかを入力してください'); return; }

        if (!window.confirm('この内容で掲載を申し込みます。\n\n運営が確認のうえ、折り返しご連絡いたします。')) return;

        setIsSending(true);
        try {
            // ★ status は必ず 'pending'。amount / paidAt は送らない（ルールで拒否される）
            await addDoc(collection(db, 'sponsor_applications'), {
                applicantId: uid,
                applicantName: userInfo?.displayName || lineUser?.displayName || '',
                companyName: form.companyName.trim(),
                contactName: form.contactName.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                categoryId: form.categoryId || 'other',
                prText: form.prText.trim(),
                url: form.url.trim(),
                couponPlan: form.couponPlan.trim(),
                transferName: form.transferName.trim(),
                note: form.note.trim(),
                source: 'form',
                status: 'pending',
                createdAt: serverTimestamp()
            });
            setIsDone(true);
        } catch (e) {
            console.error(e);
            alert('❌ 送信に失敗しました。通信環境をご確認のうえ、もう一度お試しください。');
        } finally {
            setIsSending(false);
        }
    };

    if (isDone) {
        return (
            <div className="container" style={{ paddingTop: '40px', paddingBottom: '180px', textAlign: 'center' }}>
                <div style={{ fontSize: '50px', marginBottom: '15px' }}>✅</div>
                <h2 style={{ fontSize: '18px', color: '#333', marginBottom: '10px' }}>お申し込みを受け付けました</h2>
                <p style={{ fontSize: '13px', color: '#777', lineHeight: '1.8' }}>
                    運営が内容を確認のうえ、<br />
                    お振込のご案内をご連絡いたします。<br />
                    今しばらくお待ちください。
                </p>
                <button
                    onClick={() => setActiveTab('sponsors')}
                    className="btn btn-primary"
                    style={{ marginTop: '25px', padding: '12px 30px' }}
                >
                    パートナー一覧へ戻る
                </button>
            </div>
        );
    }

    return (
        <div className="container" style={{ paddingTop: '15px', paddingBottom: '180px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, fontSize: '19px', color: '#333' }}>📝 スポンサー掲載のお申し込み</h2>
                <button
                    onClick={() => setActiveTab('sponsors')}
                    className="btn"
                    style={{ background: 'white', border: '1px solid #ddd', fontSize: '12px', padding: '6px 12px' }}
                >
                    ← 戻る
                </button>
            </div>

            {/* ★文言はすべて管理画面（表示設定タブ）で編集できる */}
            <div className="card" style={{ padding: '15px', marginBottom: '18px', background: '#f9f9f9', fontSize: '12px', color: '#666', lineHeight: '1.9' }}>
                {texts.applyIntro && (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{applyPlaceholders(texts.applyIntro, memberCount)}</div>
                )}
                {texts.applyDetails && (
                    <div style={{ marginTop: '8px', whiteSpace: 'pre-wrap', color: '#444' }}>{applyPlaceholders(texts.applyDetails, memberCount)}</div>
                )}
                {texts.applyCautions && (
                    <div style={{ marginTop: '10px', fontSize: '11px', color: '#999', whiteSpace: 'pre-wrap' }}>{applyPlaceholders(texts.applyCautions, memberCount)}</div>
                )}
            </div>

            {/* ★掲載イメージ。入力中の内容がそのまま反映される。 */}
            <div className="card" style={{ padding: '15px', marginBottom: '18px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333', marginBottom: '10px' }}>📱 掲載イメージ</div>

                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>アプリ内での表示</div>
                <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '8px', marginBottom: '14px' }}>
                    <PreviewCard name={form.companyName} catchText={form.prText} coupon={form.couponPlan} />
                </div>

                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>会員がお店で提示するクーポン画面</div>
                <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '8px', marginBottom: '14px' }}>
                    <PreviewCoupon name={form.companyName} coupon={form.couponPlan} />
                </div>
                <div style={{ fontSize: '10px', color: '#999', lineHeight: '1.7', marginBottom: '14px' }}>
                    ※ 会員名と時刻が毎秒更新されるため、スクリーンショットによる不正利用を防げます。
                </div>

                {texts.applyPlacement && (
                    <>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>掲載場所</div>
                        <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.9', whiteSpace: 'pre-wrap' }}>
                            {applyPlaceholders(texts.applyPlacement, memberCount)}
                        </div>
                    </>
                )}
            </div>

            <div className="card" style={{ padding: '18px' }}>
                <Row label="会社名・店舗名" required>
                    <input style={inputStyle} value={form.companyName} onChange={e => set('companyName', e.target.value)} placeholder="例: リベンジャーズ整骨院" />
                </Row>
                <Row label="ご担当者名" required>
                    <input style={inputStyle} value={form.contactName} onChange={e => set('contactName', e.target.value)} placeholder="例: 山田 太郎" />
                </Row>
                <Row label="メールアドレス" hint="メールか電話番号のどちらかは必須です">
                    <input style={inputStyle} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="例: info@example.com" />
                </Row>
                <Row label="電話番号">
                    <input style={inputStyle} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="例: 03-1234-5678" />
                </Row>
                <Row label="カテゴリ" required>
                    <select style={inputStyle} value={form.categoryId} onChange={e => set('categoryId', e.target.value)}>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                </Row>
                <Row label="PR文（一言）" hint="アプリのカードに表示されます（30文字程度）">
                    <input style={inputStyle} value={form.prText} onChange={e => set('prText', e.target.value)} placeholder="例: 選手の身体を支える、スポーツ専門の整骨院です" />
                </Row>
                <Row label="ホームページ・SNSのURL">
                    <input style={inputStyle} value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://..." />
                </Row>
                <Row label="ご希望のクーポン内容" hint="会員が来店時に提示します。例:「ご提示で施術料10%OFF」">
                    <textarea
                        style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                        value={form.couponPlan}
                        onChange={e => set('couponPlan', e.target.value)}
                        placeholder="例: ご提示で初回施術料20%OFF（他券併用不可）"
                    />
                </Row>
                <Row label="お振込名義" hint="ご入金の確認に使用します。会社名と異なる場合はご記入ください">
                    <input style={inputStyle} value={form.transferName} onChange={e => set('transferName', e.target.value)} placeholder="例: カ）リベンジャーズ" />
                </Row>
                <Row label="ご要望・ご質問">
                    <textarea
                        style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                        value={form.note}
                        onChange={e => set('note', e.target.value)}
                    />
                </Row>

                <button
                    onClick={handleSubmit}
                    disabled={isSending}
                    className="btn btn-primary"
                    style={{ width: '100%', padding: '15px', marginTop: '10px', background: COLORS.primary, fontSize: '15px' }}
                >
                    {isSending ? '送信中...' : 'この内容で申し込む'}
                </button>

                <div style={{ fontSize: '10px', color: '#aaa', textAlign: 'center', marginTop: '10px', lineHeight: '1.7' }}>
                    お申し込みには LINE ログインが必要です。<br />
                    ご入力いただいた情報は掲載審査とご連絡にのみ使用します。
                </div>
            </div>
        </div>
    );
};

export default SponsorApplyForm;